import { SubmissionStatus } from '@app-types';
import { Button } from '@components';
import { StepperContext } from '@components/Stepper';
import { Box, Container, Grid, Typography, useTheme } from '@mui/material';
import { useAppDispatch } from '@store';
import { getCurrentSubmission, selectCurrentSubmission } from '@store/submission/slice';
import { FC, useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import useChangeSubmissionStatusByCustomer from '../../../../shared/hooks/useChangeSubmissionStatusByCustomer';
import ConfirmModal from '../../ConfirmModal/ConfirmModal';
import Date from '../../Date/Date';
import { Wrapper } from '../styles';

interface ISubmissionAuditProps {
  status: string | undefined;
  createdAt: string | undefined;
  submissionId: string | undefined;
}

const SubmissionAudit: FC<ISubmissionAuditProps> = ({ status, createdAt, submissionId }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!submissionId) {
      dispatch(getCurrentSubmission());
    }
  }, [submissionId]);

  const currentSubmission = useSelector(selectCurrentSubmission);

  const { goToNextStep } = useContext(StepperContext);
  const { mutateAsync: changeSubmissionStatusByCustomer } = useChangeSubmissionStatusByCustomer(
    submissionId || currentSubmission?.id,
  );

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const handleOpenConfirmModal = () => setOpenConfirmModal(true);
  const handleCloseConfirmModal = () => setOpenConfirmModal(false);

  const makeWithdrawnSubmissionStatus = async () => {
    try {
      const res = await changeSubmissionStatusByCustomer({ status: SubmissionStatus.Withdrawn });

      if (res.status === SubmissionStatus.Withdrawn) {
        goToNextStep();
        dispatch(getCurrentSubmission());
      }
    } catch (error) {
      console.error('Error occurred:', error);
    }
  };

  return (
    <Container sx={{ mt: theme.spacing(5) }}>
      <Wrapper container spacing={3}>
        <Grid
          item
          xs={12}
          sx={{
            textAlign: 'center',
            margin: '0 auto',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Date createdAt={createdAt} />
          <Typography variant="h2" sx={{ fontSize: '32px', mt: '8px' }}>
            <FormattedMessage id="submissionCreation.applicationReview.submissionAudit.title" />
          </Typography>
          <Box sx={{ maxWidth: '490px' }}>
            <Typography sx={{ mt: '16px' }}>
              <FormattedMessage
                id={
                  status === SubmissionStatus.Update 
                    ? 'submissionCreation.applicationReview.submissionAudit.firstParagraph.updateStatus'
                    : 'submissionCreation.applicationReview.submissionAudit.firstParagraph'
                } 
              />
            </Typography>
            <Typography sx={{ mt: '32px' }}>
              <FormattedMessage id="submissionCreation.applicationReview.submissionAudit.secondParagraph" />
            </Typography>
          </Box>
          <Button variant="outlined" sx={{ mt: '32px' }} onClick={handleOpenConfirmModal}>
            <FormattedMessage id="submissionCreation.applicationReview.button" />
          </Button>
          {openConfirmModal && (
            <ConfirmModal
              open={openConfirmModal}
              titleId="submissionCreation.confirmModal.withdrawSubmission.title"
              handleClose={handleCloseConfirmModal}
              updateClick={() => makeWithdrawnSubmissionStatus()}
            />
          )}
        </Grid>
      </Wrapper>
    </Container>
  );
};

export default SubmissionAudit;
